import { escapeCsvCell } from "@/shared/utils/csv";

import type {
  LedgerAccount,
  LedgerEntryGroup,
  LedgerOperationKind,
  LedgerOperationView,
} from "../types/paymentsLedger.types";

export interface LedgerCsvLabels {
  /** Translated account name for a code (falls back to the backend name for an unknown one). */
  account: (account: LedgerAccount) => string;
  /** Translated entry label (reversal / reserve / settle / …). */
  entry: (entry: LedgerEntryGroup) => string;
}

/**
 * CSV of the whole journal trail of one operation — the artifact that gets handed to accounting, since the
 * runtime exposes no export endpoint.
 *
 * Money stays the raw 8-dp decimal strings so the file re-imports losslessly, and `account_code` is included
 * next to the translated label so the row is machine-matchable against the chart of accounts. Provider
 * identity is never written (rule 12): `scope_provider_code` is not a column.
 */
export function buildLedgerCsv(view: LedgerOperationView, labels: LedgerCsvLabels): string {
  const header = [
    "entry_index",
    "entry_id",
    "seq",
    "posted_at",
    "operation_id",
    "operation_type",
    "entry_label",
    "account_code",
    "account_label",
    "account_type",
    "account_currency",
    "direction",
    "amount",
    "currency",
    "balance_after",
    "description",
    "reverses_entry_id",
  ];

  const lines: string[] = [];
  for (const entry of view.entries) {
    for (const leg of entry.legs) {
      lines.push(
        [
          String(entry.index),
          leg.entry_id,
          leg.seq,
          leg.posted_at,
          leg.operation_id,
          leg.operation_type,
          escapeCsvCell(labels.entry(entry)),
          leg.account.code,
          escapeCsvCell(labels.account(leg.account)),
          leg.account.type,
          leg.account.currency,
          leg.direction,
          leg.amount,
          leg.currency,
          leg.balance_after,
          escapeCsvCell(leg.description),
          leg.reverses_entry_id ?? "",
        ].join(","),
      );
    }
  }

  return [header.join(","), ...lines].join("\n");
}

/** `ledger-payouts-3f2a9c11-2026-08-06.csv` — kind + short id + day, so a folder of exports stays sortable. */
export function ledgerCsvFilename(
  kind: LedgerOperationKind,
  operationId: string,
  now: Date = new Date(),
): string {
  return `ledger-${kind}-${operationId.slice(0, 8)}-${now.toISOString().slice(0, 10)}.csv`;
}
